import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Local unsaved-changes guard for BrowserRouter (no data-router useBlocker).
 *
 * Guard-owned history.state keys (added alongside preserved router state):
 *   __riestroUnsavedGuard      — sentinel marker
 *   __riestroUnsavedGuardCycle — unique cycle token correlating base + sentinel
 *   __riestroUnsavedGuardRole  — "base" | "sentinel"
 *
 * Router-owned keys (idx/key/usr/…) are never overwritten with different values.
 *
 * history.go() traversal is asynchronous in real browsers. Discard never
 * inspects location/state synchronously after go(). Previous-entry availability
 * is preclassified before calling go(); otherwise fallbackTo is used alone.
 */

const GUARD_STATE_KEY = "__riestroUnsavedGuard";
const GUARD_CYCLE_KEY = "__riestroUnsavedGuardCycle";
const GUARD_ROLE_KEY = "__riestroUnsavedGuardRole";

const isModifiedClick = (event) =>
  event.metaKey
  || event.ctrlKey
  || event.shiftKey
  || event.altKey
  || event.button !== 0;

const resolveAnchorHref = (anchor) => {
  if (!anchor) return null;
  if (anchor.target && anchor.target !== "_self") return null;
  if (anchor.hasAttribute("download")) return null;
  const hrefAttr = anchor.getAttribute("href");
  if (hrefAttr == null || hrefAttr === "" || hrefAttr.startsWith("mailto:")) {
    return null;
  }
  if (hrefAttr.startsWith("#")) return null;
  try {
    return new URL(anchor.href, window.location.origin);
  } catch {
    return null;
  }
};

const isGuardState = (state) => Boolean(state && state[GUARD_STATE_KEY]);

const guardCycleOf = (state) =>
  state && typeof state === "object" ? state[GUARD_CYCLE_KEY] : undefined;

const guardRoleOf = (state) =>
  state && typeof state === "object" ? state[GUARD_ROLE_KEY] : undefined;

const sameProtectedPath = (locationLike, url) =>
  url.pathname === locationLike.pathname
  && url.search === locationLike.search;

const pathKey = (locationLike) =>
  `${locationLike.pathname}${locationLike.search || ""}`;

const spreadHistoryState = (currentState) => {
  if (currentState != null && typeof currentState === "object") {
    return { ...currentState };
  }
  return {};
};

const stripGuardMarkers = (state) => {
  if (state == null || typeof state !== "object") return state;
  const next = { ...state };
  delete next[GUARD_STATE_KEY];
  delete next[GUARD_CYCLE_KEY];
  delete next[GUARD_ROLE_KEY];
  return next;
};

const resolveHashTarget = (hashId) => {
  if (!hashId || typeof document === "undefined") return null;
  const byId = document.getElementById(hashId);
  if (byId) return byId;
  try {
    return document.querySelector(`[name="${CSS.escape(hashId)}"]`);
  } catch {
    return document.querySelector(`[name="${hashId}"]`);
  }
};

/**
 * Prove a previous traversable session entry exists before calling history.go.
 * Conservative: when unproven, callers must use fallbackTo instead of go().
 *
 *   idx > 0                         → proven router/SPA previous entry
 *   idx === 0 + canGoBack === true  → proven browser/cross-document previous entry
 *   otherwise                       → unproven (fallbackTo only)
 *
 * history.length is never used as proof of a specific previous destination.
 */
export function canProvePreviousTraversableEntry(state) {
  if (state != null && typeof state === "object") {
    const idx = state.idx;
    if (typeof idx === "number" && Number.isFinite(idx)) {
      if (idx > 0) return true;
      // idx === 0 proves only that BrowserRouter has no prior SPA entry.
      // Still consult Navigation API for a cross-document previous entry.
    }
  }
  if (
    typeof window !== "undefined"
    && window.navigation
    && typeof window.navigation.canGoBack === "boolean"
  ) {
    return window.navigation.canGoBack;
  }
  return false;
}

const expectedDestIdxFromBase = (state) => {
  if (state != null && typeof state === "object") {
    const idx = state.idx;
    if (typeof idx === "number" && Number.isFinite(idx) && idx > 0) {
      return idx - 1;
    }
  }
  return null;
};

let guardCycleSeq = 0;
const nextGuardCycleId = () => {
  guardCycleSeq += 1;
  return `ug-${Date.now().toString(36)}-${guardCycleSeq}`;
};

export function useUnsavedChangesGuard({
  when,
  bypassRef,
  fallbackTo = null,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingTx, setPendingTx] = useState(null);

  const whenRef = useRef(when);
  const locationRef = useRef(location);
  const pendingTxRef = useRef(null);
  const armedRef = useRef(false);
  const activeCycleIdRef = useRef(null);
  const pendingHistoryOpRef = useRef(null);
  const confirmedLeaveRef = useRef(null);
  const fallbackToRef = useRef(fallbackTo);
  const lastStateRef = useRef(
    typeof window !== "undefined" ? window.history.state : null,
  );

  useEffect(() => {
    whenRef.current = when;
  }, [when]);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    pendingTxRef.current = pendingTx;
  }, [pendingTx]);

  useEffect(() => {
    fallbackToRef.current = fallbackTo;
  }, [fallbackTo]);

  const clearConfirmedLeave = useCallback(() => {
    confirmedLeaveRef.current = null;
  }, []);

  const grantConfirmedLeave = useCallback((cycleId) => {
    confirmedLeaveRef.current = { cycleId, consumed: false };
  }, []);

  const navigateFallback = useCallback(() => {
    clearConfirmedLeave();
    pendingHistoryOpRef.current = null;
    armedRef.current = false;
    activeCycleIdRef.current = null;
    if (!fallbackToRef.current) {
      if (whenRef.current) {
        // No fallback available — re-arm on the edit route.
        // pushGuardEntry is wired below; call via window history arm path.
      }
      return false;
    }
    if (bypassRef) bypassRef.current = true;
    navigate(fallbackToRef.current, { replace: true });
    window.setTimeout(() => {
      if (bypassRef) bypassRef.current = false;
    }, 0);
    return true;
  }, [bypassRef, clearConfirmedLeave, navigate]);

  const pushGuardEntry = useCallback(() => {
    clearConfirmedLeave();
    const cycleId = nextGuardCycleId();
    activeCycleIdRef.current = cycleId;

    const baseState = {
      ...spreadHistoryState(window.history.state),
      [GUARD_CYCLE_KEY]: cycleId,
      [GUARD_ROLE_KEY]: "base",
    };
    delete baseState[GUARD_STATE_KEY];
    window.history.replaceState(
      baseState,
      "",
      window.location.href,
    );

    const sentinelState = {
      ...baseState,
      [GUARD_STATE_KEY]: true,
      [GUARD_CYCLE_KEY]: cycleId,
      [GUARD_ROLE_KEY]: "sentinel",
    };
    window.history.pushState(
      sentinelState,
      "",
      window.location.href,
    );
    armedRef.current = true;
    lastStateRef.current = sentinelState;
  }, [clearConfirmedLeave]);

  const stay = useCallback(() => {
    clearConfirmedLeave();
    setPendingTx(null);
    // Stay must never set bypassRef — the next navigation remains protected.
    if (whenRef.current && !armedRef.current) {
      pushGuardEntry();
    }
  }, [clearConfirmedLeave, pushGuardEntry]);

  const discard = useCallback(() => {
    const tx = pendingTxRef.current;
    setPendingTx(null);
    if (!tx) return;

    if (tx.kind === "href") {
      if (bypassRef) bypassRef.current = true;
      clearConfirmedLeave();
      const onGuardEntry = isGuardState(window.history.state);
      armedRef.current = false;
      pendingHistoryOpRef.current = null;
      activeCycleIdRef.current = null;
      navigate(tx.to, {
        ...(tx.options || {}),
        replace: onGuardEntry || Boolean(tx.options && tx.options.replace),
      });
      return;
    }

    if (tx.kind === "history") {
      const cycleId = tx.cycleId || activeCycleIdRef.current;
      const protectedPath = pathKey(locationRef.current);

      // Preclassify before any history.go — never race go() with fallback.
      if (!tx.canTraversePrevious) {
        if (!navigateFallback()) {
          if (whenRef.current) pushGuardEntry();
        }
        return;
      }

      pendingHistoryOpRef.current = {
        kind: "discard",
        cycleId,
        protectedPath,
        expectedDestIdx: tx.expectedDestIdx,
      };
      grantConfirmedLeave(cycleId);
      armedRef.current = false;
      // Traversal is asynchronous; acknowledgement happens on matching popstate.
      window.history.go(tx.delta || -1);
    }
  }, [
    bypassRef,
    clearConfirmedLeave,
    grantConfirmedLeave,
    navigate,
    navigateFallback,
    pushGuardEntry,
  ]);

  // beforeunload / pagehide for refresh, close, and cross-document leave.
  useEffect(() => {
    if (!when) return undefined;
    const onBeforeUnload = (event) => {
      if (bypassRef && bypassRef.current) return;
      const leave = confirmedLeaveRef.current;
      if (leave && !leave.consumed) {
        leave.consumed = true;
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    };
    const onPageHide = () => {
      clearConfirmedLeave();
      const op = pendingHistoryOpRef.current;
      if (op && op.kind === "discard") {
        pendingHistoryOpRef.current = null;
        armedRef.current = false;
        if (activeCycleIdRef.current === op.cycleId) {
          activeCycleIdRef.current = null;
        }
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [when, bypassRef, clearConfirmedLeave]);

  // Capture in-app link navigation + controlled same-page hash scroll.
  useEffect(() => {
    if (!when) return undefined;
    const onClick = (event) => {
      if (bypassRef && bypassRef.current) return;
      if (!whenRef.current) return;
      if (event.defaultPrevented || isModifiedClick(event)) return;
      const anchor = event.target.closest?.("a[href]");
      if (!anchor || anchor.dataset.unsavedBypass === "true") return;
      const hrefAttr = anchor.getAttribute("href");
      if (hrefAttr != null && hrefAttr.startsWith("#")) {
        event.preventDefault();
        const hashId = hrefAttr.slice(1);
        if (!hashId) return;
        const target = resolveHashTarget(hashId);
        if (target && typeof target.scrollIntoView === "function") {
          target.scrollIntoView();
        }
        const nextUrl =
          `${window.location.pathname}${window.location.search}#${hashId}`;
        window.history.replaceState(window.history.state, "", nextUrl);
        return;
      }
      const url = resolveAnchorHref(anchor);
      if (!url) return;
      if (url.origin !== window.location.origin) return;

      const nextPath = `${url.pathname}${url.search}`;
      const currentPath = `${locationRef.current.pathname}${locationRef.current.search}`;
      if (nextPath === currentPath) {
        if (url.hash && url.hash.length > 1) {
          event.preventDefault();
          const hashId = url.hash.slice(1);
          const target = resolveHashTarget(hashId);
          if (target && typeof target.scrollIntoView === "function") {
            target.scrollIntoView();
          }
          window.history.replaceState(
            window.history.state,
            "",
            `${url.pathname}${url.search}${url.hash}`,
          );
        }
        return;
      }
      if (sameProtectedPath(locationRef.current, url)) return;

      event.preventDefault();
      event.stopPropagation();
      setPendingTx({
        kind: "href",
        to: `${url.pathname}${url.search}${url.hash || ""}`,
      });
    };
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
    };
  }, [when, bypassRef]);

  // Browser back/forward coordinator — listener stays mounted while the hook
  // is alive so cleanup transitions can acknowledge even when when=false.
  useEffect(() => {
    const acknowledgeHistoryOp = (event) => {
      const op = pendingHistoryOpRef.current;
      if (!op) return false;

      const state = event.state;
      const stateCycle = guardCycleOf(state);
      const currentPath = `${window.location.pathname}${window.location.search}`;

      if (op.kind === "cleanup") {
        if (isGuardState(state)) return false;
        if (stateCycle !== op.cycleId) return false;
        if (guardRoleOf(state) !== "base") return false;
        if (currentPath !== op.protectedPath) return false;

        pendingHistoryOpRef.current = null;
        const cleaned = stripGuardMarkers(state);
        window.history.replaceState(
          cleaned,
          "",
          window.location.href,
        );
        lastStateRef.current = cleaned;
        armedRef.current = false;
        if (activeCycleIdRef.current === op.cycleId) {
          activeCycleIdRef.current = null;
        }
        if (whenRef.current && !isGuardState(window.history.state)) {
          pushGuardEntry();
        }
        return true;
      }

      if (op.kind === "discard") {
        // Wrong / unrelated events while still on the edit route must not
        // complete the operation or trigger fallback.
        if (currentPath === op.protectedPath) {
          return false;
        }

        // When a router destination index was planned, require exact identity.
        // Pathname alone is never sufficient; missing/invalid idx must not ack.
        if (op.expectedDestIdx != null) {
          if (
            state == null
            || typeof state !== "object"
            || typeof state.idx !== "number"
            || !Number.isFinite(state.idx)
            || state.idx !== op.expectedDestIdx
          ) {
            return false;
          }
        }

        pendingHistoryOpRef.current = null;
        lastStateRef.current = state;
        armedRef.current = false;
        if (activeCycleIdRef.current === op.cycleId) {
          activeCycleIdRef.current = null;
        }
        // confirmedLeave remains until beforeunload consumes it or pagehide.
        return true;
      }

      return false;
    };

    const onPopState = (event) => {
      if (bypassRef && bypassRef.current) {
        lastStateRef.current = event.state;
        return;
      }

      if (acknowledgeHistoryOp(event)) {
        return;
      }

      // Discard waiting for matching leave — ignore wrong-identity / unrelated
      // events without escaping to fallback or opening a new dialog.
      if (
        pendingHistoryOpRef.current
        && pendingHistoryOpRef.current.kind === "discard"
      ) {
        return;
      }

      if (!whenRef.current) {
        lastStateRef.current = event.state;
        return;
      }

      const previousState = lastStateRef.current;
      lastStateRef.current = event.state;

      // Forward onto the guard sentinel: quiet re-arm; clear stale Back tx.
      if (isGuardState(event.state)) {
        armedRef.current = true;
        activeCycleIdRef.current = guardCycleOf(event.state) || activeCycleIdRef.current;
        clearConfirmedLeave();
        if (pendingTxRef.current) {
          setPendingTx(null);
        }
        return;
      }

      const stillOnProtected =
        window.location.pathname === locationRef.current.pathname
        && window.location.search === locationRef.current.search;

      if (stillOnProtected) {
        if (
          isGuardState(previousState)
          && guardCycleOf(previousState)
          && (
            guardCycleOf(event.state) === guardCycleOf(previousState)
            || guardRoleOf(event.state) === "base"
          )
        ) {
          armedRef.current = false;
          const baseState = event.state;
          setPendingTx({
            kind: "history",
            delta: -1,
            cycleId: guardCycleOf(previousState),
            canTraversePrevious: canProvePreviousTraversableEntry(baseState),
            expectedDestIdx: expectedDestIdxFromBase(baseState),
          });
        }
        return;
      }

      // Left the protected route unexpectedly — restore edit URL and prompt.
      const escapedTo = `${window.location.pathname}${window.location.search}${window.location.hash || ""}`;
      const protectedTo = `${locationRef.current.pathname}${locationRef.current.search}${locationRef.current.hash || ""}`;
      if (bypassRef) bypassRef.current = true;
      navigate(protectedTo, { replace: true });
      window.setTimeout(() => {
        if (bypassRef) bypassRef.current = false;
      }, 0);
      armedRef.current = false;
      clearConfirmedLeave();
      setPendingTx({
        kind: "href",
        to: escapedTo,
        options: { replace: true },
      });
    };

    window.addEventListener("popstate", onPopState);

    if (!when) {
      setPendingTx(null);
      clearConfirmedLeave();
      if (
        armedRef.current
        && isGuardState(window.history.state)
        && !pendingHistoryOpRef.current
      ) {
        const cycleId = guardCycleOf(window.history.state) || activeCycleIdRef.current;
        pendingHistoryOpRef.current = {
          kind: "cleanup",
          cycleId,
          protectedPath: pathKey(locationRef.current),
        };
        armedRef.current = false;
        window.history.back();
      } else if (!pendingHistoryOpRef.current) {
        armedRef.current = false;
      }
    } else if (!armedRef.current && !isGuardState(window.history.state)) {
      if (!pendingHistoryOpRef.current) {
        pushGuardEntry();
      }
    } else if (isGuardState(window.history.state)) {
      armedRef.current = true;
      activeCycleIdRef.current = guardCycleOf(window.history.state) || activeCycleIdRef.current;
      lastStateRef.current = window.history.state;
    }

    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [
    when,
    bypassRef,
    clearConfirmedLeave,
    navigate,
    pushGuardEntry,
  ]);

  useEffect(() => () => {
    pendingHistoryOpRef.current = null;
    clearConfirmedLeave();
  }, [clearConfirmedLeave]);

  return {
    isPromptOpen: Boolean(pendingTx),
    stay,
    discard,
  };
}
